import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useParams } from "react-router-dom";

import { HANDBOOK_INITIAL_LOAD_COUNT, HANDBOOK_SHOW_MORE_LIMIT } from "pages/HandBook/const";
import { useCurrentTemplateStructure } from "pages/HandBook/hooks/useCurrentTemplateStructure";
import { HANDBOOK_TEMPLATE_LVL } from "pages/HandBook/model/slice";
import {
  getHandbookTemplateExpenditures,
  getHandbookTemplateExpendituresMore,
  getHandbookTemplateSections,
  getHandbookTemplates,
} from "pages/HandBook/model/thunks";
import { IHandBookTemplateExpenditure } from "pages/HandBook/model/types";
import { handbooksModifyWithEditings } from "pages/HandBook/utils/handbooksModifyWithEditings";
import { findSegmentsById } from "pages/HandBook/utils/segments";

import { useTypedSelector } from "app/store/typedUseSelector";

import { useQueryParams } from "utils/hooks/useQueryParams";

import { generateStorageKey } from "utils/helpers/generateStorageKey";

export const useHandbookTemplateExpenditures = () => {
  const history = useHistory();
  const { templateId, sectionId, subSectionId } = useParams<{
    templateId: string;
    sectionId?: string;
    subSectionId?: string;
  }>();

  const segmentId = useQueryParams("segment");

  const [filter, setFilter] = useState<Record<string, string | undefined>>(() => ({
    ...(segmentId ? { segment_id: segmentId, template_id: templateId } : { section_id: subSectionId }),
  }));
  const hasListFilters = Object.keys(filter).length > 2;
  const { limit, offset, ...restFilter } = filter;
  const key = generateStorageKey(restFilter as any);
  const moreKey = generateStorageKey({
    ...restFilter,
    type: "moreTemplateSections",
  } as any);
  const dispatch = useDispatch();
  const cacheKey = useTypedSelector((state) => state.handBookReducer.invalidationKey);

  const expenditures = useTypedSelector((state) => state.handBookReducer.templateExpenditures)[key];
  const templateEditings = useTypedSelector((state) => state.handBookReducer.templateEditings);
  const editings = templateEditings[HANDBOOK_TEMPLATE_LVL.EXPENDITURES];
  const isEditing = templateEditings.isEditing;

  const { currentTemplate, currentSection, currentSubSection, segment, parentSegment } = useCurrentTemplateStructure({
    templateId,
    sectionId,
    subSectionId,
    segmentId,
  });

  const isLoading = useTypedSelector((state) => state.handBookReducer.loadings)[key];
  const isLoadingMore = useTypedSelector((state) => state.handBookReducer.loadings)[moreKey];
  const isLoadingApplyEditings = useTypedSelector((state) => state.handBookReducer.loadings)[
    "applyEditingHandbookTemplates"
  ];

  const displayExpenditures: IHandBookTemplateExpenditure[] = useMemo(() => {
    if (!expenditures?.results) return expenditures?.results;
    return handbooksModifyWithEditings(expenditures.results, editings);
  }, [expenditures?.results, editings.deletions, editings.new, cacheKey]);

  useEffect(() => {
    dispatch(
      getHandbookTemplateExpenditures({
        ...filter,
        limit: Math.max(expenditures?.results?.length ?? HANDBOOK_SHOW_MORE_LIMIT, HANDBOOK_SHOW_MORE_LIMIT) + 1,
      })
    );
  }, [cacheKey, filter]);

  const onPagination = () => {
    dispatch(
      getHandbookTemplateExpendituresMore({
        ...filter,
        offset: expenditures?.results?.length,
        limit: HANDBOOK_SHOW_MORE_LIMIT,
      })
    );
  };

  const onBack = () => {
    if (segmentId) {
      history.goBack();
    } else {
      history.replace(history.location.pathname.replace(/\/\d+\/?$/, ""));
    }
  };

  const onChangeFilter = useCallback(
    (name: string, value: string) => setFilter((prev) => ({ ...prev, [name]: value })),
    []
  );

  const total = useMemo(
    () =>
      (expenditures?.count || 0) -
      Object.entries(editings.deletions).filter(([id, isDeleted]) => isDeleted && Number(id) > 0).length,
    [expenditures?.count, editings.deletions]
  );

  return {
    hasListFilters,
    isLoading,
    isLoadingMore,
    isEditing,
    total,
    displayExpenditures,
    filter,
    onChangeFilter,
    onPagination,
    onBack,
    currentTemplate,
    currentSection,
    currentSubSection,
    templateId,
    sectionId,
    subSectionId,
    segment,
    parentSegment,
    isLoadingApplyEditings,
  };
};
